import { UploadedAsset } from '../types/chatTypes'

// File type validation - now includes CSV, Excel, and PowerPoint files
export const ALLOWED_FILE_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
  'application/vnd.ms-excel', // .xls
  'text/csv', // .csv
  'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
  'text/plain', // .txt
]

// Offered only when image inspection is on, so the agent can look at what it gets.
export const IMAGE_FILE_TYPES = ['image/png', 'image/jpeg', 'image/webp']
export const IMAGE_FILE_EXTENSIONS = '.png,.jpg,.jpeg,.webp'

export function allowedFileTypes(images: boolean): string[] {
  return images ? [...ALLOWED_FILE_TYPES, ...IMAGE_FILE_TYPES] : ALLOWED_FILE_TYPES
}

const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB in bytes

// Matches the number of images the agent can look at in one step.
export const MAX_FILES_PER_MESSAGE = 8

// Returns an i18n key (under the `chat` namespace) describing the failure, or
// null when the file is valid. Callers translate the key so error messages
// follow the user's selected language.
export type FileValidationError = 'fileTypeNotAllowed' | 'fileTypeNotAllowedWithImages' | 'fileTooLarge'

function validateFile(file: File, images = false): FileValidationError | null {
  if (!allowedFileTypes(images).includes(file.type)) {
    return images ? 'fileTypeNotAllowedWithImages' : 'fileTypeNotAllowed'
  }

  if (file.size > MAX_FILE_SIZE) {
    return 'fileTooLarge'
  }

  return null
}


export type AttachmentState =
  | { status: 'uploading' }
  | { status: 'done'; asset: UploadedAsset }
  | { status: 'error'; error: FileValidationError | 'documentUploadFailed' }
  | { status: 'error'; error: 'documentRejected'; message: string }

export type Attachment = { file: File } & AttachmentState

// The name the backend stores a file under: letters, digits, `.`, `_` and `-`.
const storedName = (name: string) => name.replace(/[^\p{L}\p{N}._-]/gu, '')

// Names are unique within a message because the agent refers to files by name
// and the backend stores them by it. A second "report.pdf" becomes "report (2).pdf".
export function addAttachments(
  current: Attachment[],
  files: File[],
  images: boolean,
): { added: Attachment[]; tooMany: boolean } {
  const taken = new Set(current.map(attachment => storedName(attachment.file.name)))
  const room = Math.max(0, MAX_FILES_PER_MESSAGE - current.length)
  const added = files.slice(0, room).map((picked): Attachment => {
    const dot = picked.name.lastIndexOf('.')
    const [base, ext] = dot > 0 ? [picked.name.slice(0, dot), picked.name.slice(dot)] : [picked.name, '']
    let file = picked
    for (let n = 2; taken.has(storedName(file.name)); n++) {
      file = new File([picked], `${base} (${n})${ext}`, { type: picked.type, lastModified: picked.lastModified })
    }
    taken.add(storedName(file.name))
    const error = validateFile(file, images)
    return error ? { file, status: 'error', error } : { file, status: 'uploading' }
  })
  return { added, tooMany: files.length > room }
}

// Image file type validation
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp']

export function validateImageFile(file: File): string | null {
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    return 'Only JPG, PNG, and WEBP images are allowed'
  }

  if (file.size > MAX_FILE_SIZE) {
    return 'File size must be less than 10MB'
  }

  return null
}
